import { useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Settings as SettingsIcon, Building2, FileText, Pen, Plus, Trash2, Loader2, Save, Eye, Copy, FlaskConical, RotateCcw, ShieldCheck, Lock, User, Code2, Sparkles, ArrowUpRight } from "lucide-react";
import { UpgradeModal } from "@/components/upgrade/UpgradeModal";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useStaffIndustryView, INDUSTRY_OPTIONS } from "@/hooks/useStaffIndustryView";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { useCompanySettings } from "@/hooks/useCompanySettings";
import { useContractTemplates } from "@/hooks/useContractTemplates";
import { CLEANING_CONTRACT_TEMPLATES } from "@/lib/industries/cleaning/templates";
import { PI_CONTRACT_TEMPLATES } from "@/lib/industries/private-investigation/templates";
import { CONSTRUCTION_CONTRACT_TEMPLATES } from "@/lib/industries/construction/templates";
import { LANDSCAPING_CONTRACT_TEMPLATES } from "@/lib/industries/landscaping/templates";
import { PEST_CONTROL_CONTRACT_TEMPLATES } from "@/lib/industries/pest-control/templates";
import { HOME_SERVICES_CONTRACT_TEMPLATES } from "@/lib/industries/home-services/templates";
import { POWER_WASHING_CONTRACT_TEMPLATES } from "@/lib/industries/power-washing/templates";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";
import { PREMADE_TEMPLATES, type PremadeTemplate } from "@/lib/premade-contract-templates";
import { useDemoMode } from "@/hooks/useDemoMode";
import SupportAccessToggle from "@/components/settings/SupportAccessToggle";
import ProfileSettings from "@/components/settings/ProfileSettings";
import EnterpriseApiManager from "@/components/settings/EnterpriseApiManager";
import EnterpriseSelfServe from "@/components/settings/EnterpriseSelfServe";
import { Link } from "@/lib/router-compat";

const getIndustryTemplates = (industry: string): PremadeTemplate[] => {
  if (industry === "cleaning" || industry === "commercial-cleaning") return CLEANING_CONTRACT_TEMPLATES;
  if (industry === "private-investigation") return PI_CONTRACT_TEMPLATES;
  if (industry === "construction-services") return CONSTRUCTION_CONTRACT_TEMPLATES;
  if (industry === "landscaping") return LANDSCAPING_CONTRACT_TEMPLATES;
  if (industry === "pest-control") return PEST_CONTROL_CONTRACT_TEMPLATES;
  if (industry === "home-services") return HOME_SERVICES_CONTRACT_TEMPLATES;
  return PREMADE_TEMPLATES;
};

const getIndustryTemplateDescription = (industry: string): string => {
  if (industry === "cleaning" || industry === "commercial-cleaning") return "Ready-to-use contract templates for commercial cleaning services.";
  if (industry === "private-investigation") return "Ready-to-use contract templates for private investigation services.";
  if (industry === "event-staffing") return "Ready-to-use contract templates for event staffing services.";
  if (industry === "construction-services") return "Ready-to-use contract templates for construction services.";
  if (industry === "landscaping") return "Ready-to-use contract templates for landscaping and groundskeeping services.";
  if (industry === "pest-control") return "Ready-to-use contract templates for pest control services.";
  if (industry === "home-services") return "Ready-to-use contract templates for home improvement services.";
  return "Ready-to-use contract templates for common security services.";
};

const DEFAULT_TEMPLATE = `SECURITY SERVICES CONTRACT

This Security Services Contract ("Contract") is entered into as of {{date}} ("Effective Date") by and between:

PROVIDER:
{{company_name}}
{{company_address}}
Phone: {{company_phone}} | Email: {{company_email}}
(hereinafter referred to as "Provider")

CLIENT:
{{client_name}}
{{client_company}}
Email: {{client_email}} | Phone: {{client_phone}}
(hereinafter referred to as "Client")

Collectively referred to as the "Parties."

──────────────────────────────────────────────────

1. INTRODUCTION AND PURPOSE

This Contract sets forth the terms and conditions under which the Provider shall furnish security services to the Client. Both Parties agree to be bound by the terms contained herein, effective as of the date first written above.

──────────────────────────────────────────────────

2. SCOPE OF SERVICES

The Provider agrees to perform the following services for the Client:

{{scope_of_work}}

Project Description:
{{project_description}}

Additional Notes:
{{proposal_notes}}

──────────────────────────────────────────────────

3. RESPONSIBILITIES

A. Provider Responsibilities:
   - Provide qualified, licensed, and insured security personnel as described in the Scope of Services.
   - Maintain all required licenses, certifications, and insurance coverage for the duration of this Contract.
   - Supervise and manage deployed personnel to ensure compliance with Client expectations and contractual obligations.
   - Provide timely written reports, incident documentation, and communication as required.

B. Client Responsibilities:
   - Provide the Provider with accurate and complete information regarding the premises, risks, and any known hazards.
   - Grant reasonable access to facilities and resources necessary for the Provider to perform the agreed-upon services.
   - Notify the Provider promptly of any changes in conditions, threats, or requirements that may affect the scope of work.
   - Comply with all payment obligations as set forth in this Contract.

──────────────────────────────────────────────────

4. COMPENSATION AND PAYMENT TERMS

The Client agrees to compensate the Provider as follows:

Estimated Hours: {{estimated_hours}}
Estimated Total Cost: \${{estimated_cost}}

Payment Terms: {{payment_terms}}

All invoices shall be delivered in writing (electronic or print). The Client agrees to remit payment within the period specified above. Late payments shall incur a fee of 1.5% per month on the outstanding balance. The Provider reserves the right to suspend services if payment is not received within fifteen (15) days past the due date.

──────────────────────────────────────────────────

5. TERM AND TIMELINE

This Contract shall commence on the Effective Date and shall remain in effect for the duration of the agreed service period unless terminated earlier in accordance with Section 7.

Any extension or renewal of this Contract shall require written agreement by both Parties.

──────────────────────────────────────────────────

6. CONFIDENTIALITY

Both Parties agree to maintain the confidentiality of all proprietary and sensitive information exchanged during the term of this Contract. This obligation shall survive the termination of this Contract for a period of two (2) years.

──────────────────────────────────────────────────

7. TERMINATION

Either Party may terminate this Contract with thirty (30) days' written notice to the other Party. In the event of a material breach by either Party, the non-breaching Party may terminate this Contract immediately upon written notice, provided that the breaching Party has been given a reasonable opportunity to cure the breach (not to exceed ten (10) days).

Upon termination, the Client shall pay for all services rendered through the termination date.

──────────────────────────────────────────────────

8. LIABILITY AND INDEMNIFICATION

A. The Provider shall maintain commercial general liability insurance with coverage limits reasonably acceptable to the Client.
B. The Provider shall not be liable for any indirect, incidental, or consequential damages arising from the performance of services under this Contract.
C. Each Party agrees to indemnify and hold harmless the other Party from and against any claims, damages, losses, or expenses arising from the indemnifying Party's negligence or willful misconduct.

──────────────────────────────────────────────────

9. GOVERNING LAW AND DISPUTE RESOLUTION

This Contract shall be governed by and construed in accordance with the laws of the state in which the services are performed. Any disputes arising under this Contract shall first be submitted to mediation. If mediation fails, disputes shall be resolved through binding arbitration in accordance with applicable rules.

──────────────────────────────────────────────────

10. GENERAL PROVISIONS

A. This Contract constitutes the entire agreement between the Parties and supersedes all prior discussions, negotiations, and agreements.
B. No amendment or modification of this Contract shall be valid unless made in writing and signed by both Parties.
C. If any provision of this Contract is found to be unenforceable, the remaining provisions shall continue in full force and effect.
D. Neither Party may assign this Contract without the prior written consent of the other Party.

──────────────────────────────────────────────────

11. ADDITIONAL TERMS AND CONDITIONS

{{terms_and_conditions}}

──────────────────────────────────────────────────

12. SIGNATURES

By signing below, the Parties acknowledge that they have read, understood, and agree to be bound by the terms and conditions set forth in this Contract.


PROVIDER:

Name: {{company_name}}
Title: Authorized Representative
Signature: _________________________ Date: __________


CLIENT:

Name: {{client_name}}
Title: Authorized Representative
Signature: _________________________ Date: __________
`;

const Settings = () => {
  const { settings, setSettings, isLoading: settingsLoading, isSaving, saveSettings } = useCompanySettings();
  const { templates, isLoading: templatesLoading, saveTemplate, deleteTemplate } = useContractTemplates();
  const [editingTemplate, setEditingTemplate] = useState<{ id?: string; name: string; description: string; content: string } | null>(null);
  const [previewingPremade, setPreviewingPremade] = useState<PremadeTemplate | null>(null);
  const { isDemoUser, demoTier, resetDemo } = useDemoMode();
  const { isSuperAdmin, isSupportRole, subscription } = useAuth();
  const isStaffOrAdmin = isSuperAdmin || isSupportRole;
  const isEnterprise = subscription.tier === "enterprise" || subscription.tier === "testing";
  const [showApiUpgrade, setShowApiUpgrade] = useState(false);
  const { activeIndustry, setActiveIndustry } = useStaffIndustryView();

  const demoTierLabel = demoTier === "starter_demo" ? "Starter" : demoTier === "professional_demo" ? "Professional" : "Agency";

  const handleSaveSettings = () => saveSettings(settings);

  const openNewTemplate = () => {
    setEditingTemplate({ name: "", description: "", content: DEFAULT_TEMPLATE });
  };

  const openEditTemplate = (t: typeof templates[0]) => {
    setEditingTemplate({ id: t.id, name: t.name, description: t.description || "", content: t.content });
  };

  const handleSaveTemplate = async () => {
    if (!editingTemplate?.name.trim()) {
      toast({ title: "Template name required", variant: "destructive" });
      return;
    }
    await saveTemplate(editingTemplate);
    setEditingTemplate(null);
  };

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto space-y-6">
        <h1 className="text-3xl font-bold text-foreground">Settings</h1>

        <Tabs defaultValue="profile" className="w-full">
          <div className="overflow-x-auto -mx-1 px-1">
            <TabsList className="inline-flex w-auto min-w-full sm:w-full gap-1">
              <TabsTrigger value="profile" className="gap-1.5 text-xs sm:text-sm whitespace-nowrap"><User className="h-3.5 w-3.5" /> Profile</TabsTrigger>
              {(!isSupportRole || isSuperAdmin) && (
                <>
                  <TabsTrigger value="company" className="gap-1.5 text-xs sm:text-sm whitespace-nowrap"><Building2 className="h-3.5 w-3.5" /> Company</TabsTrigger>
                  <TabsTrigger value="templates" className="gap-1.5 text-xs sm:text-sm whitespace-nowrap"><FileText className="h-3.5 w-3.5" /> Templates</TabsTrigger>
                  <TabsTrigger value="signature" className="gap-1.5 text-xs sm:text-sm whitespace-nowrap"><Pen className="h-3.5 w-3.5" /> Signature</TabsTrigger>
                </>
              )}
              <TabsTrigger value="privacy" className="gap-1.5 text-xs sm:text-sm whitespace-nowrap"><Lock className="h-3.5 w-3.5" /> Privacy</TabsTrigger>
              {(!isSupportRole || isSuperAdmin) && (
                <TabsTrigger value="integrations" className="gap-1.5 text-xs sm:text-sm whitespace-nowrap"><Code2 className="h-3.5 w-3.5" /> Integrations</TabsTrigger>
              )}
              {isStaffOrAdmin && (
                <TabsTrigger value="staff" className="gap-1.5 text-xs sm:text-sm whitespace-nowrap"><ShieldCheck className="h-3.5 w-3.5" /> Staff</TabsTrigger>
              )}
              {isEnterprise && (
                <TabsTrigger value="enterprise" className="gap-1.5 text-xs sm:text-sm whitespace-nowrap"><Sparkles className="h-3.5 w-3.5" /> Enterprise</TabsTrigger>
              )}
              {isDemoUser && (
                <TabsTrigger value="demo" className="gap-1.5 text-xs sm:text-sm whitespace-nowrap"><FlaskConical className="h-3.5 w-3.5" /> Demo</TabsTrigger>
              )}
            </TabsList>
          </div>

          {/* Profile Settings */}
          <TabsContent value="profile">
            <ProfileSettings />
          </TabsContent>

          {/* Company Settings */}
          <TabsContent value="company">
            <Card className="border-border bg-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-foreground">
                  <Building2 className="h-5 w-5 text-primary" /> Company Information
                </CardTitle>
                <CardDescription>These defaults auto-fill into new proposals and contracts.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {settingsLoading ? (
                  <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-muted-foreground">Company Name</Label>
                        <Input value={settings.company_name} onChange={(e) => setSettings({ ...settings, company_name: e.target.value })} placeholder="Your Security Company" className="mt-1 bg-secondary border-border" />
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Company Email</Label>
                        <Input value={settings.company_email} onChange={(e) => setSettings({ ...settings, company_email: e.target.value })} placeholder="info@company.com" className="mt-1 bg-secondary border-border" />
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Company Phone</Label>
                        <Input value={settings.company_phone} onChange={(e) => setSettings({ ...settings, company_phone: e.target.value })} placeholder="(555) 123-4567" className="mt-1 bg-secondary border-border" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Company Address</Label>
                      <Textarea value={settings.company_address} onChange={(e) => setSettings({ ...settings, company_address: e.target.value })} placeholder="123 Main St, City, State ZIP" className="mt-1 bg-secondary border-border" rows={2} />
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Default Payment Terms</Label>
                      <Input value={settings.default_payment_terms} onChange={(e) => setSettings({ ...settings, default_payment_terms: e.target.value })} placeholder="Net 30" className="mt-1 bg-secondary border-border" />
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Default Contract Terms & Conditions</Label>
                      <Textarea value={settings.default_terms} onChange={(e) => setSettings({ ...settings, default_terms: e.target.value })} className="mt-1 bg-secondary border-border" rows={4} />
                    </div>
                    <Button onClick={handleSaveSettings} disabled={isSaving} className="gap-2">
                      {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                      Save Company Settings
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Contract Templates */}
          <TabsContent value="templates">
            <Card className="border-border bg-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-foreground">
                    <FileText className="h-5 w-5 text-primary" /> Contract Templates
                  </CardTitle>
                  <CardDescription>Create reusable templates with variables like {"{{client_name}}"}, {"{{company_name}}"}, etc.</CardDescription>
                </div>
                <Button size="sm" onClick={openNewTemplate} className="gap-1">
                  <Plus className="h-4 w-4" /> New Template
                </Button>
              </CardHeader>
              <CardContent>
                {templatesLoading ? (
                  <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
                ) : templates.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No templates yet. Create your first contract template.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {templates.map((t) => (
                      <div key={t.id} className="flex items-center justify-between p-3 rounded-lg border border-border bg-secondary/50">
                        <div className="cursor-pointer flex-1" onClick={() => openEditTemplate(t)}>
                          <p className="font-medium text-foreground">{t.name}</p>
                          {t.description && <p className="text-sm text-muted-foreground">{t.description}</p>}
                          <p className="text-xs text-muted-foreground mt-1">Created {new Date(t.created_at).toLocaleDateString()}</p>
                        </div>
                        <Button variant="ghost" size="icon" onClick={() => deleteTemplate(t.id)} className="text-destructive hover:text-destructive">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Pre-made Templates */}
            <Card className="border-border bg-card mt-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-foreground">
                  <Copy className="h-5 w-5 text-primary" /> Pre-Made Templates
                </CardTitle>
                <CardDescription>
                  {getIndustryTemplateDescription(activeIndustry)} Preview them and add to your templates with one click.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {getIndustryTemplates(activeIndustry).map((pt, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-lg border border-border bg-secondary/50 gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-foreground">{pt.name}</p>
                        <p className="text-sm text-muted-foreground">{pt.description}</p>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <Button variant="outline" size="sm" onClick={() => setPreviewingPremade(pt)} className="gap-1">
                          <Eye className="h-4 w-4" /> Preview
                        </Button>
                        <Button size="sm" onClick={async () => {
                          await saveTemplate({ name: pt.name, description: pt.description, content: pt.content });
                        }} className="gap-1">
                          <Plus className="h-4 w-4" /> Use
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Pre-made Template Preview Dialog */}
            <Dialog open={!!previewingPremade} onOpenChange={(open) => !open && setPreviewingPremade(null)}>
              <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{previewingPremade?.name}</DialogTitle>
                </DialogHeader>
                {previewingPremade && (
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">{previewingPremade.description}</p>
                    <pre className="whitespace-pre-wrap text-sm font-mono bg-secondary/50 p-4 rounded-lg border border-border max-h-[500px] overflow-y-auto">
                      {previewingPremade.content}
                    </pre>
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" onClick={() => setPreviewingPremade(null)}>Close</Button>
                      <Button onClick={async () => {
                        await saveTemplate({ name: previewingPremade.name, description: previewingPremade.description, content: previewingPremade.content });
                        setPreviewingPremade(null);
                      }} className="gap-1">
                        <Plus className="h-4 w-4" /> Add to My Templates
                      </Button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>

            {/* Template Editor Dialog */}
            <Dialog open={!!editingTemplate} onOpenChange={(open) => !open && setEditingTemplate(null)}>
              <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingTemplate?.id ? "Edit Template" : "New Contract Template"}</DialogTitle>
                </DialogHeader>
                {editingTemplate && (
                  <div className="space-y-4">
                    <div>
                      <Label>Template Name</Label>
                      <Input value={editingTemplate.name} onChange={(e) => setEditingTemplate({ ...editingTemplate, name: e.target.value })} placeholder="Standard Security Contract" className="mt-1" />
                    </div>
                    <div>
                      <Label>Description (optional)</Label>
                      <Input value={editingTemplate.description} onChange={(e) => setEditingTemplate({ ...editingTemplate, description: e.target.value })} placeholder="Basic contract for on-site security" className="mt-1" />
                    </div>
                    <div>
                      <Label>Template Content</Label>
                      <div className="text-xs text-muted-foreground mb-1 space-y-1">
                        <p className="font-medium">Company: {"{{company_name}}"}, {"{{company_address}}"}, {"{{company_phone}}"}, {"{{company_email}}"}</p>
                        <p className="font-medium">Client: {"{{client_name}}"}, {"{{client_company}}"}, {"{{client_email}}"}, {"{{client_phone}}"}</p>
                        <p className="font-medium">Proposal: {"{{project_description}}"}, {"{{scope_of_work}}"}, {"{{estimated_hours}}"}, {"{{estimated_cost}}"}, {"{{proposal_notes}}"}</p>
                        <p className="font-medium">Contract: {"{{date}}"}, {"{{payment_terms}}"}, {"{{terms_and_conditions}}"}</p>
                      </div>
                      <Textarea
                        value={editingTemplate.content}
                        onChange={(e) => setEditingTemplate({ ...editingTemplate, content: e.target.value })}
                        className="mt-1 font-mono text-sm"
                        rows={20}
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" onClick={() => setEditingTemplate(null)}>Cancel</Button>
                      <Button onClick={handleSaveTemplate}>Save Template</Button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Signature Settings */}
          <TabsContent value="signature">
            <Card className="border-border bg-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-foreground">
                  <Pen className="h-5 w-5 text-primary" /> Signature & Signing
                </CardTitle>
                <CardDescription>Configure how contracts are signed and capture client signatures digitally.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="rounded-lg border border-border p-4 bg-secondary/30">
                  <h3 className="font-medium text-foreground mb-2">How Contract Signing Works</h3>
                  <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside">
                    <li>Create a contract from a template in the <strong>Contract Generator</strong> page.</li>
                    <li>Fill in client details and customize the contract content.</li>
                    <li>Generate a unique signing link to send to your client.</li>
                    <li>The client reviews the contract and signs using a digital signature pad.</li>
                    <li>The signed contract appears in your contract history with the signature captured.</li>
                  </ol>
                </div>
                <div className="rounded-lg border border-border p-4 bg-secondary/30">
                  <h3 className="font-medium text-foreground mb-2">Supported Features</h3>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>✅ Canvas-based digital signature capture</li>
                    <li>✅ Unique secure signing links per contract</li>
                    <li>✅ Contract status tracking (draft → sent → signed)</li>
                    <li>✅ PDF export of signed contracts</li>
                    <li>✅ Template variable substitution</li>
                  </ul>
                </div>
                <Button asChild>
                  <a href="/contracts">Go to Contract Generator →</a>
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
          {/* Privacy & Support Access */}
          <TabsContent value="privacy">
            <SupportAccessToggle />
          </TabsContent>

          {/* Integrations / API Access */}
          <TabsContent value="integrations">
            <Card className="border-border bg-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-foreground">
                  <Code2 className="h-5 w-5 text-primary" /> API Access
                  <Badge className="bg-primary/10 text-primary border-primary/30 text-[10px] gap-1 ml-1">
                    <Sparkles className="h-2.5 w-2.5" />
                    Enterprise
                  </Badge>
                </CardTitle>
                <CardDescription>
                  Programmatically create and fetch proposals from your own systems.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {isEnterprise ? (
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">
                      Manage scoped keys, webhooks, and view delivery logs below. See the{" "}
                      <Link to="/enterprise-api-docs" className="text-primary hover:underline">full API docs</Link>.
                    </p>
                  </div>
                ) : (
                  <div className="rounded-xl border border-dashed border-primary/30 bg-primary/5 p-5 space-y-3">
                    <p className="text-sm text-foreground">
                      Build integrations with your CRM, dispatch system, or internal tools using REST endpoints across all 8 industries — with scoped keys, AI generation, and HMAC-signed webhooks.
                    </p>
                    <ul className="space-y-1.5 text-sm text-muted-foreground">
                      <li className="flex items-start gap-2"><span className="text-primary mt-0.5">•</span> REST endpoints (proposals, threats, contracts, inquiries)</li>
                      <li className="flex items-start gap-2"><span className="text-primary mt-0.5">•</span> Scoped, hashed API keys</li>
                      <li className="flex items-start gap-2"><span className="text-primary mt-0.5">•</span> Webhook events with signature verification</li>
                    </ul>
                    <div className="flex gap-2 flex-wrap">
                      <Button onClick={() => setShowApiUpgrade(true)} className="gradient-primary text-primary-foreground shadow-glow hover:opacity-90">
                        Unlock API Access <ArrowUpRight className="ml-1.5 h-4 w-4" />
                      </Button>
                      <Button asChild variant="outline">
                        <Link to="/enterprise-api-docs">View docs</Link>
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            {isEnterprise && <div className="mt-6"><EnterpriseApiManager /></div>}
          </TabsContent>

          {/* Staff Settings */}
          {isStaffOrAdmin && (
            <TabsContent value="staff">
              <Card className="border-border bg-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-foreground">
                    <ShieldCheck className="h-5 w-5 text-primary" /> Staff Settings
                  </CardTitle>
                  <CardDescription>Staff and admin settings for managing your view across all industries.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-3">
                    <Label className="text-muted-foreground">Active Industry View</Label>
                    <p className="text-sm text-muted-foreground">
                      Switch the industry you're viewing. This changes the dashboard, proposal builder, and templates shown throughout the platform.
                    </p>
                    <Select value={activeIndustry} onValueChange={setActiveIndustry}>
                      <SelectTrigger className="w-full max-w-xs bg-secondary border-border">
                        <SelectValue placeholder="Select industry" />
                      </SelectTrigger>
                      <SelectContent>
                        {INDUSTRY_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="rounded-lg border border-border p-4 bg-secondary/30">
                    <h3 className="font-medium text-foreground mb-3">Available Industries</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {INDUSTRY_OPTIONS.map((opt) => (
                        <div
                          key={opt.value}
                          className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${
                            activeIndustry === opt.value
                              ? "border-primary bg-primary/5"
                              : "border-border bg-secondary/50 hover:border-muted-foreground"
                          }`}
                          onClick={() => setActiveIndustry(opt.value)}
                        >
                          <span className="font-medium text-foreground">{opt.label}</span>
                          {activeIndustry === opt.value && (
                            <Badge variant="default" className="text-xs">Active</Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Enterprise Self-Serve */}
          {isEnterprise && (
            <TabsContent value="enterprise">
              <EnterpriseSelfServe />
            </TabsContent>
          )}

          {/* Demo Controls */}
          {isDemoUser && (
            <TabsContent value="demo">
              <Card className="border-border bg-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-foreground">
                    <FlaskConical className="h-5 w-5 text-warning" /> Demo Controls
                  </CardTitle>
                  <CardDescription>Manage your demo mode settings. These controls are only visible to demo users.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="rounded-lg border border-border p-4 bg-secondary/30">
                    <h3 className="font-medium text-foreground mb-2">Current Demo Plan</h3>
                    <p className="text-2xl font-bold text-primary">{demoTierLabel}</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      This simulates the {demoTierLabel} plan experience. Click locked features to auto-upgrade.
                    </p>
                  </div>
                  <div className="rounded-lg border border-border p-4 bg-secondary/30">
                    <h3 className="font-medium text-foreground mb-2">How Demo Mode Works</h3>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      <li>🔒 Locked features show "Click to unlock" instead of upgrade buttons</li>
                      <li>🔓 Clicking a locked feature auto-upgrades to the required demo plan</li>
                      <li>🚫 No Stripe charges, billing, or real subscription changes</li>
                      <li>🔄 Use the Reset button below to return to Starter demo</li>
                    </ul>
                  </div>
                  <Button
                    variant="outline"
                    onClick={resetDemo}
                    className="gap-2 border-warning text-warning hover:bg-warning/10"
                  >
                    <RotateCcw className="h-4 w-4" />
                    Reset Demo
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>
      <UpgradeModal
        open={showApiUpgrade}
        onOpenChange={setShowApiUpgrade}
        feature="api-access"
      />
    </AppLayout>
  );
};

export default Settings;
